import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { Observable, of } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { AppStateService } from '../../../core/services/app-state.service';
import { IntakeFormViewComponent } from 'src/app/intake-form/intake-form-view/intake-form-view.component';
import { IntakeFormService } from 'src/app/intake-form/intake-form.service';
import { ReferralService } from 'src/app/referral/referral.service';
import { PatientModalComponent } from 'src/app/shared/consult-forms/patient-modal/patient-modal.component';
import {
  HealthCardMode,
  LocationHealthCardModeMap
} from 'src/app/shared/location-select/location.model';
import { Doctor, NameFormat, QuestionnaireType } from '../../../../API';
import { compareDateStrings } from '../../../core/api/date.util';
import { Patient, PatientService } from '../../../core/api/patient.service';
import { StaffService } from '../../../core/api/staff.service';
import { SendQuestionnaireDialogComponent } from '../../../shared/send-questionnaire-dialog/send-questionnaire-dialog.component';
import { InteractionsComponent } from '../interactions/interactions.component';
import { PatientFileModalComponent } from './../patient-file/patient-file-modal/patient-file-modal.component';
import { PatientDeleteDialogComponent } from './patient-delete-dialog/patient-delete-dialog.component';
import { MatMenuTrigger, MatTooltip } from '@angular/material';
import { safeParseJson } from 'src/app/shared/util/helper.util';
import { HoverMenuHelper } from 'src/app/shared/util/hover-menu.util';
import { hasMissingEnrollmentFields } from '../../patient-enrollment.util';
import {
  buildPatientAppHistory,
  buildPatientAppState,
  getPatientAppBadgeClass,
  PatientAppEnrolmentCycle,
  PatientAppState
} from '../../patient-app-timeline.util';
import { TranslocoService } from '@ngneat/transloco';

import { PampIframeService } from 'src/app/core/services/pamp-iframe.service';
import { SideSheetService } from 'src/app/shared/side-sheet/side-sheet.service';
import { features } from 'src/app/core/features';
import {
  buildPatientAppActions,
  buildPatientAppAdvisory,
  PatientAppAction,
  PatientAppActionId
} from '../../patient-app-actions.util';

export interface PatientAppNode {
  labelKey: string;
  date: string | null;
  badgeClass?: string;
  isCurrent?: boolean;
}

// A patient in these states has never used the app, so "last activity" cannot be shown for them.
// The rail has room for three dated nodes; a longer cycle keeps its start and its two newest
// distinct events, so a repeated event fills one node with its most recent occurrence.
const MAX_RAIL_NODES = 3;

const ACTION_RESULT_SNACKBAR_DURATION_MS = 3000;

@Component({
  selector: 'csi-patient-card',
  templateUrl: './patient-card.component.html',
  styleUrls: ['./patient-card.component.scss']
})
export class PatientCardComponent implements OnInit, OnDestroy {
  public readonly features = features;

  private _patient: Patient;
  public patientAppStatusClass = 'badge-gray';
  public patientAppNodes: PatientAppNode[] = [];
  public patientAppActions: PatientAppAction[] = [];
  // Translated once here (with markup) because the popover renders it as HTML.
  public patientAppAdvisoryMessage = '';
  public isPatientMissingEnrollmentFields = false;
  public missingEnrollmentFieldsMessage = '';
  @Input() set patient(patient: Patient) {
    this._patient = patient;
    if (this.patient) {
      this.patient.patientAppEnrollmentConfig = safeParseJson(
        this.patient.patientAppEnrollmentConfig
      );
    }
    const enrollmentStatus = hasMissingEnrollmentFields(patient, this.translocoService);
    this.isPatientMissingEnrollmentFields = enrollmentStatus.isMissingFields;
    this.missingEnrollmentFieldsMessage = enrollmentStatus.message;

    const patientAppState = buildPatientAppState(this.patient);
    this.patientAppStatusClass = patientAppState.badgeClass;

    const patientAppHistory = buildPatientAppHistory(this.patient);
    this.patientAppNodes = this.buildPatientAppNodes(
      patientAppState,
      patientAppHistory.cycles[0]
    );
    this.patientAppActions = buildPatientAppActions(this.patient);
    this.patientAppAdvisoryMessage = this.translatePatientAppAdvisory();
    if (this.patient && this.patient.address) {
      const { address, city, province, country, postalCode } = this.patient;
      let isFirst = true;
      this.fullAddress = '';
      [address, city, province, country, postalCode]
        .filter(item => !!item)
        .forEach(item => {
          this.fullAddress += (isFirst ? '' : ', ') + item;
          isFirst = false;
        });
    } else {
      this.fullAddress = ''; // Fixes issue with address not updating when switching patients
    }
    this.init(); // Handle case where component does not re-initialize when switching patients through search bar.
  }
  get patient() {
    return this._patient;
  }
  @Input() showSendQuestionnaireButton = true;
  @Input() readOnly: boolean;
  @Input() printMode: boolean;

  @Output() patientFileSaved: EventEmitter<any> = new EventEmitter();

  public readonly NameFormat = NameFormat;
  public readonly HealthCardMode = HealthCardMode;
  public healthCardConfig: HealthCardMode;
  public isRefer: boolean;
  public intakeFormConfig: any = {};
  public showPatientFileButton = false;
  public fullAddress = '';
  public externalPatientID = '';
  public matToolTipText = '';
  public isPatientAppActive$: Observable<boolean>;

  private loggedInDoctor: Doctor;

  private readonly defaultQuestionnaireType = QuestionnaireType.DEQ;
  private readonly LocationHealthCardModeMap = LocationHealthCardModeMap;
  @ViewChild('interactions', { static: false }) patientInteraction: InteractionsComponent;
  @ViewChild('tooltip', { static: false }) tooltip: MatTooltip;

  public hoverMenu = new HoverMenuHelper();

  constructor(
    private dialog: MatDialog,
    private sideSheetService: SideSheetService,
    public staffService: StaffService,
    public patientService: PatientService,
    private intakeService: IntakeFormService,
    public referralService: ReferralService,
    private appStateService: AppStateService,
    private pampIframeService: PampIframeService,
    private translocoService: TranslocoService,
    private snackBar: MatSnackBar
  ) {
    this.isPatientAppActive$ = this.appStateService.isPatientAppActive$;
  }

  ngOnInit() {
    this.init();
    this.staffService
      .getLoggedInStaff()
      .pipe(untilDestroyed(this))
      .subscribe(doctor => {
        this.loggedInDoctor = doctor;
        this.healthCardConfig = this.LocationHealthCardModeMap[doctor.clinic.country];
      });

    this.externalPatientID = this.patient.externalPatientId || '';
    this.matToolTipText = this.externalPatientID;

    this.translocoService.langChanges$.pipe(untilDestroyed(this)).subscribe(() => {
      if (this._patient) {
        const status = hasMissingEnrollmentFields(this._patient, this.translocoService);
        this.isPatientMissingEnrollmentFields = status.isMissingFields;
        this.missingEnrollmentFieldsMessage = status.message;
        this.patientAppAdvisoryMessage = this.translatePatientAppAdvisory();
      }
    });

    // PAMP closes its overlay as soon as the resend dialog is dismissed, so the confirmation has
    // to be shown from here or the clinic never sees it.
    this.pampIframeService.actionResult$.pipe(untilDestroyed(this)).subscribe(result => {
      if (result.action === 'resendInvite' && result.success) {
        this.snackBar.open(this.translocoService.translate('Invitation resent'), '', {
          duration: ACTION_RESULT_SNACKBAR_DURATION_MS
        });
      }
    });
  }

  init() {
    this.intakeFormConfig =
      this.patientService.intakeForms && this.patientService.intakeForms.length > 0
        ? this.patientService.intakeForms[0]
        : null;

    this.isRefer = window.location.href.includes('/refer');

    // A clinic that only refers out has the flag explicitly false; an unset flag predates it
    // and still means full access.
    const clinic = this.staffService.staff && this.staffService.staff.clinic;
    const hasNoDryEyePortalAccess = !!clinic && clinic.hasDryEyePortalAccess === false;

    this.showPatientFileButton =
      !hasNoDryEyePortalAccess &&
      (this.staffService.isDoctor ||
        this.staffService.isClinicOwner ||
        this.staffService.isClinicAdmin);
  }

  ngOnDestroy() {
    this.hoverMenu.destroy();
  }

  public onEditClick() {
    PatientModalComponent.open(this.dialog, this.loggedInDoctor, this.patient, null);
  }

  public onSendQuestionnaireClick() {
    this.sideSheetService.open(
      SendQuestionnaireDialogComponent,
      SendQuestionnaireDialogComponent.buildDialogData(
        this.defaultQuestionnaireType,
        this.patient,
        null,
        true,
        true
      )
    );
  }

  public onOpenPatientFileClick() {
    setTimeout(() => {
      const patientFileDialogRef = this.dialog.open(PatientFileModalComponent, {
        width: '60rem',
        minWidth: '15rem',
        height: '80vh',
        data: this.patient
      });
      patientFileDialogRef.afterClosed().subscribe(nonModifiableData => {
        if (nonModifiableData) {
          this.patientFileSaved.emit(nonModifiableData);
        }
      });
    });
  }

  public onDeleteClick() {
    this.dialog.open(PatientDeleteDialogComponent, {
      width: '40rem',
      data: this.patient
    });
  }

  public openCreateInteractionsModal() {
    this.patientInteraction.openCreateInteractionsModal();
  }

  public openIntakeForm() {
    this.intakeService
      .getIntakeForm(this.intakeFormConfig.id)
      .pipe(
        mergeMap(patientIntakeForm => {
          if (patientIntakeForm) {
            IntakeFormViewComponent.openIntakeFormDialog(this.dialog, patientIntakeForm);
          }
          return of(null);
        }),
        catchError((err, caught) => {
          throw new Error('Error' + err + caught);
        })
      )
      .subscribe();
  }

  public onCopy() {
    this.matToolTipText = 'Copied';
    this.tooltip.show();
    setTimeout(() => (this.matToolTipText = this.externalPatientID), 2500);
    setTimeout(() => {
      this.tooltip.show();
    });
  }

  public openPatientApp(isDefault = false) {
    if (!this.patient) {
      return;
    }
    if (!this.patient.patientAppId) {
      this.pampIframeService.openPatientEnrollment(this.patient.id, isDefault);
    } else {
      this.pampIframeService.openPatientDashboard(this.patient.id);
    }
  }

  public onPatientAppAction(actionId: PatientAppActionId) {
    if (!this.patient || !this.patient.patientAppId) {
      return;
    }
    switch (actionId) {
      case 'resendInvite':
        this.pampIframeService.openPatientDashboardAction(this.patient.id, 'resendInvite');
        break;
      case 'quickReEnroll':
        this.pampIframeService.openPatientReEnrollment(this.patient.id, true);
        break;
      case 'customReEnroll':
        this.pampIframeService.openPatientReEnrollment(this.patient.id, false);
        break;
      default:
        break;
    }
  }

  public trackByPatientAppNode(index: number, node: PatientAppNode): string {
    return node.labelKey;
  }

  public trackByPatientAppAction(index: number, action: PatientAppAction): string {
    return action.id;
  }

  private translatePatientAppAdvisory(): string {
    const advisory = buildPatientAppAdvisory(this._patient);
    return advisory ? this.translocoService.translate(advisory.messageKey) : '';
  }

  // The rail is Invited -> Last activity -> current status. A step only appears when it actually
  // happened, so an invitation that expired never shows activity it could not have had.
  // The rail shows the CURRENT enrolment only. Older cycles live in the patient app column's
  // activity tab.
  private buildPatientAppNodes(
    patientAppState: PatientAppState,
    currentCycle: PatientAppEnrolmentCycle
  ): PatientAppNode[] {
    const cycleEvents = currentCycle ? currentCycle.events : [];
    if (cycleEvents.length === 0) {
      // No timeline to read: the status is still known, and the rail has always ended on it.
      if (!patientAppState.statusKey) {
        return [];
      }
      return [
        {
          labelKey: patientAppState.statusKey,
          date: patientAppState.currentStatusDate,
          badgeClass: this.patientAppStatusClass,
          isCurrent: true
        }
      ];
    }

    const nodes: PatientAppNode[] = cycleEvents.map(cycleEvent => ({
      labelKey: cycleEvent.eventLabel,
      date: cycleEvent.date
    }));

    const lastNode = nodes[nodes.length - 1];
    const isStatusMissingFromTimeline =
      !!patientAppState.statusKey && lastNode.labelKey !== patientAppState.statusKey;
    const statusAgeAgainstLastEvent =
      patientAppState.currentStatusDate && lastNode.date
        ? compareDateStrings(patientAppState.currentStatusDate, lastNode.date)
        : 0;
    const isStatusNoOlderThanLastEvent =
      isNaN(statusAgeAgainstLastEvent) || statusAgeAgainstLastEvent >= 0;
    if (isStatusMissingFromTimeline && isStatusNoOlderThanLastEvent) {
      nodes.push({
        labelKey: patientAppState.statusKey,
        date: patientAppState.currentStatusDate
      });
    }

    // Walking back from the newest event keeps the most recent occurrence of each repeated one,
    // so the rail spends its remaining nodes on events that actually say something different.
    const newestDistinctIndexes: number[] = [];
    const newestDistinctLabels: string[] = [];
    for (
      let index = nodes.length - 1;
      index >= 0 && newestDistinctIndexes.length < MAX_RAIL_NODES - 1;
      index--
    ) {
      if (newestDistinctLabels.indexOf(nodes[index].labelKey) === -1) {
        newestDistinctLabels.push(nodes[index].labelKey);
        newestDistinctIndexes.push(index);
      }
    }

    const railIndexes = [0]
      .concat(newestDistinctIndexes.reverse())
      .filter((index, position, indexes) => indexes.indexOf(index) === position)
      .filter((index, position, indexes) =>
        indexes.every(
          (laterIndex, laterPosition) =>
            laterPosition <= position ||
            nodes[laterIndex].labelKey !== nodes[index].labelKey
        )
      );

    const visibleNodes = railIndexes.map(index => nodes[index]);

    // Each node is coloured by its own event, so a past Inactive or Logged Out reads as neutral
    // rather than inheriting a "step completed" green.
    return visibleNodes.map((node, index) => ({
      labelKey: node.labelKey,
      date: node.date,
      badgeClass: getPatientAppBadgeClass(node.labelKey),
      isCurrent: index === visibleNodes.length - 1
    }));
  }

  public openMenuOnHover(trigger: MatMenuTrigger): void {
    this.hoverMenu.openMenuOnHover(trigger);
  }

  public closeMenuOnLeave(trigger?: MatMenuTrigger): void {
    this.hoverMenu.closeMenuOnLeave(trigger);
  }

  public cancelMenuClose(): void {
    this.hoverMenu.cancelMenuClose();
  }
}
